import React, { useRef } from "react";

import classnames from "classnames";
import Downshift from "downshift";
import PropTypes from "prop-types";

import { useTranslation } from "components/i18n/source";
import { toSnakeCase } from "components/store/source";

import { IconMagnifyingGlass, IconX, IconCity } from "icons";

import Button from "../../atoms/Button";
import Span from "../../atoms/Span";
import Text from "../../atoms/Text";
import useMedia from "../../util/useMedia";

import styles from "./DestinationSearch.module.scss";

function DestinationSearch({
  apolloTheme,
  dropdownHeader,
  dropdownFooter,
  filterOnCurrentInput,
  getValue,
  getLabel,
  getIcon,
  inputProps,
  inNavbar,
  inputValue,
  isOpen,
  items,
  loading,
  loader,
  noIcon,
  onBlur,
  onChange,
  onClick,
  onFocus,
  onSelectedChange,
  placeholder,
  scrollIntoView,
  setInputRef,
  size,
  showCloseButton,
  onClear,
  inputElementName,
  id,
}) {
  const { t } = useTranslation();
  const isMobile = useMedia(["$tablet"], [true], false);
  const containerRef = useRef();

  const handleFocus = () => {
    if (!isMobile && scrollIntoView && containerRef.current) {
      containerRef.current.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    }
    onFocus();
  };

  const filteredItems = () =>
    inputValue
      ? items.filter(item => item.value.startsWith(toSnakeCase(inputValue)))
      : items;

  const getPlaceholder = () =>
    placeholder
      ? placeholder
      : t(
          isOpen
            ? "components.destinationSearch.example"
            : "components.destinationSearch.destinationShort_v2",
        );

  const renderDropdownContent = (
    menuProps,
    inputValue,
    itemProps,
    highlightedIndex,
  ) => {
    if (loading) return loader;

    return (
      <div className={classnames(styles.results_dropdown_container)}>
        {dropdownHeader}
        <ul {...menuProps} className={classnames(styles.results_dropdown)}>
          <ResultsDropdownItems
            inputValue={inputValue}
            getValue={getValue}
            getLabel={getLabel}
            getIcon={getIcon}
            items={filterOnCurrentInput ? filteredItems() : items}
            getItemProps={itemProps}
            highlightedIndex={highlightedIndex}
            size={size}
          />
        </ul>
        {dropdownFooter}
      </div>
    );
  };

  const handleBlur = event => {
    if (!isMobile && inputProps && inputProps.onBlur) {
      inputProps.onBlur(event);
    }
    onBlur(event);
  };

  const handleSelectedChange = event => {
    if (!event) return document.activeElement.blur();

    document.activeElement.blur();
    onSelectedChange(event);
  };

  const handleClearInput = () => {
    onChange("");
    onClear();
    handleBlur();
  };

  return (
    <Downshift
      // for SSR, an id is needed that is the same on both server and client
      // https://github.com/downshift-js/downshift/issues/602#issuecomment-429663734
      id={id}
      inputValue={inputValue}
      isOpen={isOpen}
      itemToString={item => (item ? item.label : "")}
      onSelect={handleSelectedChange}
    >
      {({
        getInputProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        getRootProps,
        highlightedIndex,
        inputValue,
      }) => (
        <div
          {...getRootProps({}, { suppressRefError: true })}
          className={classnames(
            styles.destination_search,
            styles[size],
            isOpen && styles.focused,
            apolloTheme && styles.apolloTheme,
          )}
          ref={containerRef}
        >
          {!noIcon && (
            // props should have the htmlFor prop since they're coming from Downshift's getLabellabelProps()
            // eslint-disable-next-line jsx-a11y/label-has-associated-control
            <label className={styles.search_label} {...getLabelProps()}>
              <IconMagnifyingGlass
                size="md"
                title={t("components.destinationSearch.destinationShort_v2")}
              />
            </label>
          )}
          <input
            {...getInputProps({
              onFocus: handleFocus,
              onClick,
              onBlur,
              onChange: event => {
                onChange(event.target.value);
              },
            })}
            className={classnames(
              styles.search_input,
              inNavbar && styles.navbar,
              noIcon && styles.noIcon,
              inputValue && showCloseButton && styles.showCloseButton,
            )}
            ref={setInputRef}
            placeholder={getPlaceholder()}
            {...inputProps}
            onKeyDown={event => {
              getInputProps().onKeyDown(event);
            }}
            onBlur={handleBlur}
            name={inputElementName}
            autoComplete="off"
          />
          {isOpen &&
            renderDropdownContent(
              getMenuProps(),
              inputValue,
              getItemProps,
              highlightedIndex,
            )}
          {inputValue && showCloseButton && (
            <Button
              type="no_background"
              onClick={handleClearInput}
              centered={true}
            >
              <div className={styles.clear_button}>
                <IconX size="xs" />
              </div>
            </Button>
          )}
        </div>
      )}
    </Downshift>
  );
}

const destinationSearchItemType = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
});

DestinationSearch.propTypes = {
  apolloTheme: PropTypes.bool,
  dropdownFooter: PropTypes.node,
  dropdownHeader: PropTypes.node,
  filterOnCurrentInput: PropTypes.bool,
  getValue: PropTypes.func,
  getLabel: PropTypes.func,
  getIcon: PropTypes.func,
  inNavbar: PropTypes.bool, // TODO: see if I can remove the navbar only styling
  inputValue: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(destinationSearchItemType),
  loading: PropTypes.bool,
  loader: PropTypes.element,
  noIcon: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onSelectedChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  scrollIntoView: PropTypes.bool,
  setInputRef: PropTypes.func,
  showCloseButton: PropTypes.bool,
  size: PropTypes.oneOf(["sm", ""]),
  inputElementName: PropTypes.string,
  inputProps: PropTypes.shape({ onBlur: PropTypes.func }),
  id: PropTypes.string.isRequired,
};

DestinationSearch.defaultProps = {
  filterOnCurrentInput: true,
  inNavbar: false,
  inputValue: "",
  items: [],
  loading: false,
  loader: null,
  noIcon: false,
  onBlur: () => {},
  onFocus: () => {},
  placeholder: "",
  scrollIntoView: false,
  setInputRef: () => {},
  size: "",
};

function ResultsDropdownItems({
  getItemProps,
  highlightedIndex,
  inputValue,
  getLabel,
  getIcon,
  items,
  noIcon,
  size,
}) {
  const textProps = {
    types: [size, "truncate"],
  };

  const withBoldedMatchingChars = label => (
    <>
      {/* NB: we're assuming the items' value and labels have the same length
      even though value is in snake_case and label is normal case */}
      <Span types={["medium"]}>{label.substring(0, inputValue.length)}</Span>
      {label.substring(inputValue.length)}
    </>
  );

  return items.map((item, index) => {
    const isHighlighted = (index, highlightedIndex) => {
      if (highlightedIndex === index) {
        return styles.result_item__highlighted;
      }
    };

    const itemLabel = getLabel ? getLabel(item) : item.label;
    const itemIcon = getIcon ? (
      getIcon(item)
    ) : (
      <div className={styles.city_results_icon}>
        <IconCity size="md" />
      </div>
    );

    return (
      <li
        key={index}
        className={classnames(
          styles.result_item,
          isHighlighted(index, highlightedIndex),
        )}
        {...getItemProps({ item })}
      >
        {!noIcon && itemIcon && (
          <div className={styles.results_icon}>{itemIcon}</div>
        )}
        <div className={styles.destination}>
          <Text {...textProps}>
            {inputValue ? withBoldedMatchingChars(itemLabel) : itemLabel}
          </Text>
        </div>
      </li>
    );
  });
}

ResultsDropdownItems.propTypes = {
  getItemProps: PropTypes.func.isRequired,
  highlightedIndex: PropTypes.number,
  inputValue: PropTypes.string,
  items: PropTypes.arrayOf(destinationSearchItemType),
  noIcon: PropTypes.bool,
  size: PropTypes.oneOf(["sm", ""]),
};

ResultsDropdownItems.defaultProps = {
  highlightedIndex: null,
  inputValue: "",
  items: [],
  noIcon: false,
  size: "",
};

export default DestinationSearch;
